<template>
  <div class="violation-page" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!$props.embedded" class="page-title">违规查询</h2>

      <!-- 加载状态 -->
      <div v-if="loading && list.length === 0" class="loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 空状态 -->
        <div v-if="list.length === 0" class="empty-tip">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <p class="empty-text">暂无违规记录</p>
          <p class="empty-sub">你的账号状态良好，继续保持</p>
        </div>

        <!-- 列表 -->
        <div v-else class="violation-list">
          <div
            v-for="item in list"
            :key="item.id"
            class="violation-card"
          >
            <div class="violation-header">
              <div class="violation-header-left">
                <span class="violation-type">{{ formatType(item.target_type) }}</span>
              </div>
              <span
                class="violation-status"
                :class="item.status === 1 ? 'resolved' : 'pending'"
              >
                <span class="status-indicator" :class="item.status === 1 ? 'resolved' : 'pending'"></span>
                {{ item.status === 1 ? '已处理' : '处理中' }}
              </span>
            </div>
            <div class="violation-body">
              <div class="violation-row">
                <span class="row-label">违规时间</span>
                <span class="row-value">{{ formatTime(item.createtime) }}</span>
              </div>
              <div class="violation-row">
                <span class="row-label">违规类型</span>
                <span class="row-value">{{ formatType(item.target_type) }}</span>
              </div>
              <div class="violation-row">
                <span class="row-label">违规原因</span>
                <span class="row-value reason">{{ item.reason || item.content || '--' }}</span>
              </div>
              <div v-if="item.status === 1" class="violation-row">
                <span class="row-label">处理结果</span>
                <span class="row-value result">{{ item.result || item.handle_result || '已处理' }}</span>
              </div>
              <div v-if="item.status === 1 && item.handle_time" class="violation-row">
                <span class="row-label">处理时间</span>
                <span class="row-value">{{ formatTime(item.handle_time) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="hasMore" class="load-more" @click="loadMore">
          <span>加载更多</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <div v-else-if="list.length > 0" class="load-end">
          <span class="load-end-line"></span>
          <span class="load-end-text">没有更多了</span>
          <span class="load-end-line"></span>
        </div>
      </template>
    </div>
    <PageFooter v-if="!$props.embedded" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import { getMyViolations } from '../api/violation';

export default {
  name: 'ViolationQuery',
  components: { PageFooter },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    void props.embedded;
    const list = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const hasMore = ref(true);

    const fetchList = async (pageNum = 1, append = false) => {
      loading.value = true;
      try {
        const res = await getMyViolations({ page: pageNum });
        if (res.code === 1) {
          const data = res.data;
          const items = data.list || data.data || [];
          if (append) {
            list.value = [...list.value, ...items];
          } else {
            list.value = items;
          }
          hasMore.value = items.length >= (data.per_page || 10);
          page.value = pageNum;
        }
      } catch (e) {
        console.error('获取违规记录失败', e);
      } finally {
        loading.value = false;
      }
    };

    const loadMore = () => {
      if (!hasMore.value || loading.value) return;
      fetchList(page.value + 1, true);
    };

    const formatType = (type) => {
      if (!type) return '其他';
      const map = {
        'video': '视频违规',
        'comment': '评论违规',
        'user': '用户违规',
        'post': '动态违规',
        'live': '直播违规',
        'report': '举报相关',
        '1': '视频违规',
        '2': '评论违规',
        '3': '用户违规',
        '4': '动态违规',
        '5': '直播违规'
      };
      return map[type] || type;
    };

    const formatTime = (timestamp) => {
      if (!timestamp) return '--';
      if (typeof timestamp === 'string' && !isNaN(Date.parse(timestamp))) {
        const d = new Date(timestamp);
        return d.toLocaleString('zh-CN', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false
        });
      }
      const num = Number(timestamp);
      if (isNaN(num) || num <= 0) return '--';
      const date = new Date(num * 1000);
      if (isNaN(date.getTime())) return '--';
      return date.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
    };

    onMounted(() => {
      fetchList();
    });

    return {
      list,
      loading,
      hasMore,
      loadMore,
      formatType,
      formatTime
    };
  }
};
</script>

<style scoped>
.violation-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f6f7f9 0%, #f0f2f5 100%);
  padding: 40px 0;
}
.violation-page.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}
.violation-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
.container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}
.page-title {
  font-size: 24px;
  color: #1a1a2e;
  margin-bottom: 24px;
  font-weight: 700;
  position: relative;
  padding-left: 16px;
}
.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

/* 空状态 */
.empty-tip {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
}
.empty-icon {
  margin-bottom: 16px;
  color: #d1d5db;
}
.empty-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 6px;
  font-weight: 500;
}
.empty-sub {
  font-size: 13px;
  color: #bbb;
  margin: 0;
}

/* 列表 */
.violation-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.violation-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
  transition: all 0.3s ease;
}
.violation-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.06);
  transform: translateY(-1px);
}
.violation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #f0f0f0;
}
.violation-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.violation-type {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.violation-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  padding: 4px 14px;
  border-radius: 14px;
  font-weight: 500;
}
.violation-status.pending {
  background: #fff7ed;
  color: #b45309;
}
.violation-status.resolved {
  background: #f0fdf4;
  color: #16a34a;
}
.status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.status-indicator.pending {
  background: #b45309;
}
.status-indicator.resolved {
  background: #16a34a;
}

.violation-body {
  padding: 16px 20px;
}
.violation-row {
  display: flex;
  padding: 7px 0;
  font-size: 14px;
  line-height: 1.6;
}
.row-label {
  color: #999;
  width: 80px;
  flex-shrink: 0;
  font-weight: 500;
}
.row-value {
  color: #475569;
  flex: 1;
  word-break: break-word;
}
.row-value.reason {
  color: #ef4444;
}
.row-value.result {
  color: #16a34a;
  font-weight: 500;
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px;
  color: #ff9600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}
.load-more:hover {
  color: #ff7d00;
  gap: 10px;
}
.load-end {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px;
}
.load-end-line {
  width: 40px;
  height: 1px;
  background: #e2e8f0;
}
.load-end-text {
  color: #ccc;
  font-size: 12px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: #999;
  font-size: 14px;
}
.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>